/**
 * Общие кирпичи страниц аккаунта — «Профиль», «Безопасность», «API-ключи»
 * (редизайн 15.10 под язык терминала, как «Фандинг» и «Автозалив»):
 *
 *   PageHead     — шапка-панель: значок раздела, имя, одна строка пояснения,
 *                  действия справа, ниже — плитки сводки (Kpi);
 *   Card / Row   — карточка раздела и строка «значок — что это — статус —
 *                  действие», раскрытая форма под строкой выезжает (sb-reveal);
 *   Pill         — таблетка статуса (вкл / выкл / ошибка);
 *   Fold         — свёрнутое «Дополнительно»: редкие настройки не мешают
 *                  читать главное (образец — алерты xStocks);
 *   useConfirm   — подтверждение окном вместо window.confirm: что случится —
 *                  словами, главная кнопка справа, опасная — красная.
 *
 * Стили — строкой рядом (PK_CSS), общий index.css не трогаем.
 */
import React, { useCallback, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import clsx from 'clsx';
import { ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Modal, ModalHeader, ModalBody, ModalFooter } from '../../ui/Modal';
import { Button } from '../../ui/Button';

export const PK_CSS = `
.pk-wrap{max-width:1040px}
.pk-card{animation-duration:.4s}
.pk-row{position:relative;transition:background-color .18s}
.pk-row:hover{background:hsl(var(--foreground) / .025)}
.pk-row::before{content:"";position:absolute;left:0;top:14px;bottom:14px;width:3px;border-radius:0 3px 3px 0;background:transparent;transition:background-color .25s,box-shadow .25s}
.pk-row[data-tone="green"]::before{background:hsl(var(--trade-green));box-shadow:0 0 10px hsl(var(--trade-green) / .7)}
.pk-row[data-tone="accent"]::before{background:hsl(var(--primary));box-shadow:0 0 10px hsl(var(--primary) / .7)}
.pk-row[data-tone="red"]::before{background:hsl(var(--trade-red));box-shadow:0 0 10px hsl(var(--trade-red) / .7)}
.pk-row[data-tone="warn"]::before{background:rgb(234 179 8);box-shadow:0 0 10px rgb(234 179 8 / .6)}
.pk-ico{display:grid;place-items:center;width:36px;height:36px;flex-shrink:0;border-radius:11px;color:hsl(var(--muted-foreground));
  background:hsl(var(--foreground) / .055);box-shadow:inset 0 0 0 1px hsl(var(--border));transition:color .25s,background-color .25s,box-shadow .25s,transform .25s cubic-bezier(.3,1.4,.4,1)}
.pk-row:hover .pk-ico{transform:scale(1.05)}
.pk-ico[data-tone="green"]{color:hsl(var(--trade-green));background:hsl(var(--trade-green) / .1);box-shadow:inset 0 0 0 1px hsl(var(--trade-green) / .3)}
.pk-ico[data-tone="accent"]{color:hsl(var(--primary));background:hsl(var(--primary) / .1);box-shadow:inset 0 0 0 1px hsl(var(--primary) / .3)}
.pk-ico[data-tone="red"]{color:hsl(var(--trade-red));background:hsl(var(--trade-red) / .1);box-shadow:inset 0 0 0 1px hsl(var(--trade-red) / .3)}
.pk-ico[data-tone="warn"]{color:rgb(202 138 4);background:rgb(234 179 8 / .12);box-shadow:inset 0 0 0 1px rgb(234 179 8 / .35)}
.pk-pill{display:inline-flex;align-items:center;gap:5px;height:20px;padding:0 8px;border-radius:999px;font-size:11px;font-weight:600;white-space:nowrap;
  color:hsl(var(--muted-foreground));background:hsl(var(--foreground) / .07)}
.pk-pill>i{width:6px;height:6px;border-radius:999px;background:currentColor;flex-shrink:0}
.pk-pill[data-tone="green"]{color:hsl(var(--trade-green));background:hsl(var(--trade-green) / .12)}
.pk-pill[data-tone="green"]>i{box-shadow:0 0 6px hsl(var(--trade-green))}
.pk-pill[data-tone="accent"]{color:hsl(var(--primary));background:hsl(var(--primary) / .12)}
.pk-pill[data-tone="red"]{color:hsl(var(--trade-red));background:hsl(var(--trade-red) / .12)}
.pk-pill[data-tone="warn"]{color:rgb(202 138 4);background:rgb(234 179 8 / .14)}
.dark .pk-pill[data-tone="warn"],.dark .pk-ico[data-tone="warn"]{color:rgb(250 204 21)}
.pk-form{border-radius:12px;border:1px solid hsl(var(--border));background:hsl(var(--foreground) / .025);padding:12px}
.pk-lbl{display:block;margin-bottom:4px;font-size:12px;font-weight:500;color:hsl(var(--muted-foreground))}
.pk-hint{font-size:12px;line-height:1.45;color:hsl(var(--muted-foreground))}
.pk-copy{display:inline-flex;max-width:100%;align-items:center;gap:6px;border-radius:9px;padding:4px 9px;font-family:theme('fontFamily.mono');font-size:13px;font-weight:600;
  border:1px solid hsl(var(--border));background:hsl(var(--background) / .5);transition:border-color .2s,background-color .2s,transform .15s}
.pk-copy:hover{border-color:hsl(var(--primary) / .5);background:hsl(var(--primary) / .07)}
.pk-copy:active{transform:scale(.97)}
.pk-copy svg{flex-shrink:0;opacity:.6}
.pk-banner{display:flex;align-items:flex-start;gap:10px;border-radius:14px;padding:12px 14px;font-size:13px;line-height:1.45;
  border:1px solid rgb(234 179 8 / .45);background:linear-gradient(135deg,rgb(234 179 8 / .14),rgb(234 179 8 / .05));animation:sb-rise .45s cubic-bezier(.2,.8,.2,1) both}
.pk-banner>svg{flex-shrink:0;margin-top:1px;color:rgb(202 138 4)}
.dark .pk-banner>svg{color:rgb(250 204 21)}
.pk-stagger>*{animation:pk-in .35s cubic-bezier(.2,.8,.2,1) both}
.pk-stagger>*:nth-child(2){animation-delay:40ms}.pk-stagger>*:nth-child(3){animation-delay:80ms}
.pk-stagger>*:nth-child(4){animation-delay:120ms}.pk-stagger>*:nth-child(5){animation-delay:160ms}
.pk-stagger>*:nth-child(n+6){animation-delay:200ms}
@keyframes pk-in{from{opacity:0;transform:translateY(4px)}to{opacity:1;transform:none}}
.pk-skel{border-radius:8px;background:linear-gradient(90deg,hsl(var(--foreground) / .05),hsl(var(--foreground) / .1),hsl(var(--foreground) / .05));
  background-size:200% 100%;animation:pk-skel 1.3s ease-in-out infinite}
@keyframes pk-skel{from{background-position:100% 0}to{background-position:-100% 0}}
:root:not(.dark) .pk-row:hover{background:hsl(var(--primary) / .035)}
:root:not(.dark) .pk-form{background:hsl(var(--background) / .6)}
:root:not(.dark) .pk-copy{background:hsl(var(--card))}
@media (prefers-reduced-motion:reduce){
  .pk-stagger>*,.pk-skel,.pk-banner{animation:none !important}
  .pk-row,.pk-row::before,.pk-ico,.pk-copy{transition:none !important}
}
`;

export type Tone = 'green' | 'red' | 'accent' | 'warn' | 'muted';

/** Обёртка страницы аккаунта: ширина, ритм, стили кита. */
export const AccountShell: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className }) => (
  <div className={clsx('pk-wrap mx-auto w-full space-y-3 px-3 py-4 text-foreground sm:px-6 sm:py-5', className)}>
    <style>{PK_CSS}</style>
    {children}
  </div>
);

/** Шапка-панель: значок, имя, пояснение, действия; ниже — плитки (children). */
export const PageHead: React.FC<{
  icon: React.ReactNode; title: React.ReactNode; desc?: React.ReactNode;
  actions?: React.ReactNode; children?: React.ReactNode;
}> = ({ icon, title, desc, actions, children }) => (
  <div className="sb-panel !overflow-visible px-3 py-3 sm:px-4">
    <div className="flex items-center gap-3">
      <span className="sb-badgeicon">{icon}</span>
      <div className="min-w-0 flex-1">
        <h1 className="truncate text-lg font-semibold tracking-tight">{title}</h1>
        {desc && <p className="hidden truncate text-[12px] text-muted-foreground sm:block">{desc}</p>}
      </div>
      {actions && <div className="flex shrink-0 items-center gap-1.5">{actions}</div>}
    </div>
    {children && <div className="mt-3">{children}</div>}
  </div>
);

/** Плитка сводки (тот же вид, что в «Фандинге»). С onClick — кнопка. */
export const Kpi: React.FC<{
  icon: React.ReactNode; label: string; value: React.ReactNode; hint?: React.ReactNode; title?: string;
  tone?: Tone; className?: string; onClick?: () => void; mono?: boolean;
}> = ({ icon, label, value, hint, title, tone, className, onClick, mono = true }) => {
  const body = (<>
    <div className="sb-kpi-label">{icon}<span className="truncate">{label}</span></div>
    <div className={clsx('sb-kpi-value', !mono && '!font-sans !text-[16px]',
      tone === 'green' ? 'text-trade-green' : tone === 'red' ? 'text-trade-red'
        : tone === 'warn' ? 'text-yellow-600 dark:text-yellow-400' : 'text-foreground')}>{value}</div>
    {hint && <div className="truncate text-[11px] text-muted-foreground">{hint}</div>}
  </>);
  const dt = tone === 'warn' ? 'accent' : tone;
  return onClick
    ? <button type="button" title={title} data-tone={dt} onClick={onClick} className={clsx('sb-kpi text-left', className)}>{body}</button>
    : <div title={title} data-tone={dt} className={clsx('sb-kpi', className)}>{body}</div>;
};

/** Таблетка статуса с точкой. */
export const Pill: React.FC<{ tone?: Tone; children: React.ReactNode; title?: string; dot?: boolean }> =
  ({ tone = 'muted', children, title, dot = true }) => (
  <span className="pk-pill" data-tone={tone} title={title}>{dot && <i/>}{children}</span>
);

/** Карточка раздела: шапка (значок, имя, одна строка пояснения, действие) и строки. */
export const Card: React.FC<{
  icon?: React.ReactNode; title: React.ReactNode; hint?: React.ReactNode; action?: React.ReactNode;
  children?: React.ReactNode; id?: string; className?: string; bodyClassName?: string; delay?: 2 | 3 | 4;
}> = ({ icon, title, hint, action, children, id, className, bodyClassName, delay }) => (
  <section id={id} className={clsx('sb-panel pk-card scroll-mt-20', delay && `sb-panel-${delay}`, className)}>
    <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2 border-b border-border/70 px-3 py-3 sm:px-4">
      <div className="flex min-w-0 items-center gap-2.5">
        {icon && <span className="text-primary [&_svg]:size-4">{icon}</span>}
        <div className="min-w-0">
          <h2 className="text-[15px] font-semibold leading-tight">{title}</h2>
          {hint && <p className="mt-0.5 text-[12px] text-muted-foreground">{hint}</p>}
        </div>
      </div>
      {action && <div className="flex shrink-0 flex-wrap items-center gap-2">{action}</div>}
    </div>
    {children != null && <div className={clsx('divide-y divide-border/60', bodyClassName)}>{children}</div>}
  </section>
);

/** Строка: значок, имя + статус, пояснение, действие справа; форма — под строкой. */
export const Row: React.FC<{
  icon: React.ReactNode; title: React.ReactNode; desc?: React.ReactNode; badge?: React.ReactNode;
  action?: React.ReactNode; tone?: Tone; children?: React.ReactNode; className?: string; id?: string;
}> = ({ icon, title, desc, badge, action, tone, children, className, id }) => {
  // `{open && <form/>}` даёт false — «пусто» проверяем по настоящим узлам.
  const body = React.Children.toArray(children);
  const actions = React.Children.toArray(action);
  return (
    <div id={id} className={clsx('pk-row scroll-mt-24 px-3 py-3 sm:px-4', className)} data-tone={tone}>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <span className="pk-ico [&_svg]:size-[17px]" data-tone={tone}>{icon}</span>
        <div className="min-w-0 flex-1 basis-[180px]">
          <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-[14px] font-medium">{title}{badge}</div>
          {desc && <div className="mt-0.5 text-[12.5px] leading-snug text-muted-foreground">{desc}</div>}
        </div>
        {actions.length > 0 && <div className="flex shrink-0 flex-wrap items-center gap-1.5 max-sm:w-full max-sm:justify-end">{actions}</div>}
      </div>
      {body.length > 0 && <div className="sb-reveal mt-3 sm:ml-[48px]">{body}</div>}
    </div>
  );
};

/** Свёрнутое «Дополнительно». */
export const Fold: React.FC<{ title: React.ReactNode; hint?: React.ReactNode; children: React.ReactNode; defaultOpen?: boolean; className?: string }> =
  ({ title, hint, children, defaultOpen, className }) => {
  const [open, setOpen] = useState(!!defaultOpen);
  return (
    <div className={clsx('overflow-hidden rounded-[12px] border border-border', className)}>
      <button type="button" onClick={() => setOpen(v => !v)} aria-expanded={open}
        className="sb-fold flex items-center gap-2 px-3 py-2.5 text-left text-[13px] font-medium">
        <span className="shrink-0">{title}</span>
        {hint && <span className="min-w-0 truncate text-[12px] font-normal text-muted-foreground">{hint}</span>}
        <ChevronDown size={14} className="sb-chev ml-auto shrink-0 text-muted-foreground" data-open={open}/>
      </button>
      {open && <div className="sb-reveal border-t border-border">{children}</div>}
    </div>
  );
};

/** Строка настройки внутри Fold/карточки: подпись + пояснение, управление справа. */
export const SetRow: React.FC<{ title: React.ReactNode; hint?: React.ReactNode; wide?: boolean; children: React.ReactNode }> =
  ({ title, hint, wide, children }) => (
  <div className={clsx('flex gap-3 px-3 py-2.5', wide ? 'flex-col' : 'items-center')}>
    <div className="min-w-0 flex-1">
      <div className="text-[13px]">{title}</div>
      {hint && <div className="text-[12px] text-muted-foreground">{hint}</div>}
    </div>
    <div className={clsx(!wide && 'shrink-0')}>{children}</div>
  </div>
);

/** Скелет строки списка на время загрузки. */
export const SkeletonRows: React.FC<{ n?: number }> = ({ n = 2 }) => (
  <>
    {Array.from({ length: n }, (_, i) => (
      <div key={i} className="flex items-center gap-3 px-3 py-3.5 sm:px-4">
        <span className="pk-skel h-9 w-9 shrink-0"/>
        <div className="flex-1 space-y-1.5">
          <div className="pk-skel h-3.5 w-40 max-w-[60%]"/>
          <div className="pk-skel h-3 w-64 max-w-[85%]"/>
        </div>
      </div>
    ))}
  </>
);

/** Пустое состояние внутри карточки: значок, одна строка, действие. */
export const Empty: React.FC<{ icon: React.ReactNode; title: React.ReactNode; hint?: React.ReactNode; action?: React.ReactNode }> =
  ({ icon, title, hint, action }) => (
  <div className="flex flex-col items-center gap-2 px-4 py-8 text-center">
    <span className="pk-ico !h-11 !w-11 [&_svg]:size-5" data-tone="accent">{icon}</span>
    <div className="text-[14px] font-medium">{title}</div>
    {hint && <div className="max-w-sm text-[12.5px] text-muted-foreground">{hint}</div>}
    {action && <div className="mt-1">{action}</div>}
  </div>
);

/* ------------------------------ подтверждение ------------------------------ */

export interface ConfirmOpts { title: string; body?: React.ReactNode; cta: string; danger?: boolean }

/**
 * `const [ask, confirmUi] = useConfirm()` → `if (!(await ask({...}))) return;`
 * Поток действия не меняется: это то же «да/нет», что window.confirm, только
 * окном в стиле приложения. confirmUi — отрисовать где угодно в компоненте.
 */
export function useConfirm(): [(o: ConfirmOpts) => Promise<boolean>, React.ReactNode] {
  const { t } = useTranslation('common');
  const [opts, setOpts] = useState<ConfirmOpts | null>(null);
  const resolver = useRef<((v: boolean) => void) | null>(null);
  const ask = useCallback((o: ConfirmOpts) => new Promise<boolean>(res => {
    resolver.current?.(false);
    resolver.current = res;
    setOpts(o);
  }), []);
  const done = (v: boolean) => { resolver.current?.(v); resolver.current = null; setOpts(null); };
  // Порталом в body: панели анимируются transform'ом, и fixed-окно внутри
  // них позиционировалось бы от панели, а не от экрана.
  const ui = opts && createPortal(
    <Modal onClose={() => done(false)} className="max-w-md">
      <ModalHeader title={opts.title}/>
      {opts.body && <ModalBody><div className="text-[14px] leading-relaxed text-muted-foreground">{opts.body}</div></ModalBody>}
      <ModalFooter>
        <Button variant="ghost" size="sm" onClick={() => done(false)}>{t('cancel')}</Button>
        <Button variant={opts.danger ? 'destructive' : 'default'} size="sm" autoFocus onClick={() => done(true)}>
          {opts.cta}
        </Button>
      </ModalFooter>
    </Modal>,
    document.body,
  );
  return [ask, ui];
}
